import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { SearchTransactionTypeOptions } from "byzantine/src/Transaction";
import {
  convertFiltersToTags,
  presetPeriods,
} from "byzantine/src/TransactionSearchAndFilter";
import type {
  Filter,
  PossibleFilters,
} from "byzantine/src/TransactionSearchAndFilter";
import Filters from "byzantine/src/filters";
import { Button, formatNumber, Tag, TextInput } from "@narmi/design_system";
import { ContextForm, Dialog, useFormData } from "cerulean";
import AmountTextInput from "../form/AmountTextInput";
import DateRangeFormField from "../form/DateRangeFormField";
import DropdownField from "../form/DropdownField";

const parseAmount = (value?: string) => {
  if (!value || value === "$") return null;

  const parsedValue = parseFloat(value.replace(/[$,]/g, ""));
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

interface TransactionFilterTagSectionProps {
  searchFilters: PossibleFilters;
  setSearchFilters: (newState: object) => void;
}

export const TransactionFilterTagSection = ({
  searchFilters,
  setSearchFilters,
}: TransactionFilterTagSectionProps) => {
  const { l10n } = useLocalization();
  /* display active filters as deleteable tags */
  const filterArray = convertFiltersToTags(searchFilters, formatNumber);
  if (filterArray.length === 0) return null;

  const formattedMinAmount = searchFilters?.minAmount
    ? formatNumber(Number(searchFilters.minAmount.replace(/[$,]/g, "")))
    : "";
  const formattedMaxAmount = searchFilters?.maxAmount
    ? formatNumber(Number(searchFilters.maxAmount.replace(/[$,]/g, "")))
    : "";

  const getLocalizedLabel = (filter: { type: Filter; value: string }) => {
    switch (filter.type) {
      case "minAmount":
        return l10n.getString("filter-tag-min-amount", {
          amount: formattedMinAmount,
        });
      case "maxAmount":
        return l10n.getString("filter-tag-max-amount", {
          amount: formattedMaxAmount,
        });
      case "amountRange":
        return l10n.getString("filter-tag-amount-range", {
          minAmount: formattedMinAmount,
          maxAmount: formattedMaxAmount,
        });
      default:
        return filter.value;
    }
  };

  const deleteFilter = (filterType: Filter) => {
    setSearchFilters((currentState: PossibleFilters) => {
      const updatedState = { ...currentState };

      /* `amountRange` filter type is the combination of minAmount and maxAmount fields,
         which are treated as 1 filter if they are both set */
      if (filterType === "amountRange") {
        delete updatedState.minAmount;
        delete updatedState.maxAmount;
      } else {
        delete updatedState[filterType];
      }

      // clear `period` if it is set to `custom` and both `min_date` and `max_date` have been cleared
      if (
        updatedState?.period === "custom" &&
        !updatedState?.min_date &&
        !updatedState?.max_date
      ) {
        delete updatedState.period;
      }

      return updatedState;
    });
  };

  return (
    <div className="tags-row search">
      {filterArray.map((filter) => (
        <Tag
          key={filter.type}
          kind="dismissible"
          label={getLocalizedLabel(filter)}
          onDismiss={() => deleteFilter(filter.type)}
        />
      ))}
      <Button
        kind="plain"
        label={l10n.getString("button-clear-all", null, "Clear all")}
        testId="tags-row-clear-all-filters"
        onClick={() => setSearchFilters({})}
      />
    </div>
  );
};

const MinMaxAmountField = ({ errorText = "" }: { errorText?: string }) => {
  const { l10n } = useLocalization();

  return (
    <div className="margin--bottom--l">
      <div style={{ display: "flex" }}>
        <ContextForm.Field style={{ marginBottom: "0px" }}>
          <AmountTextInput
            field="minAmount"
            label={l10n.getString("label-minimum", null, "Min")}
            testId="min-amount-input"
            data-test-element="min-amount-input"
          />
        </ContextForm.Field>
        <div className="margin--x--xs fontSize--s" style={{ display: "flex" }}>
          <div style={{ margin: "auto" }}>
            {l10n.getString("date-range-separator", null, "to")}
          </div>
        </div>
        <ContextForm.Field style={{ marginBottom: "0px" }}>
          <AmountTextInput
            field="maxAmount"
            label={l10n.getString("label-maximum", null, "Max")}
            testId="max-amount-input"
            data-test-element="max-amount-input"
          />
        </ContextForm.Field>
      </div>
      {errorText ? (
        <div className="fontSize--s margin--top--xxs fontColor--error">
          {errorText}
        </div>
      ) : null}
    </div>
  );
};

interface TransactionSearchAndFilterProps {
  searchFilters: PossibleFilters;
  setSearchFilters: (newState: PossibleFilters) => void;
  switchToAllTransactionsTab: () => void;
  searchableCategories: string[];
  searchableTags: string[];
}

interface SearchAndFilterFormProps extends TransactionSearchAndFilterProps {
  closeDialog: () => void;
}

const SearchAndFilterForm = ({
  closeDialog,
  searchFilters,
  setSearchFilters,
  switchToAllTransactionsTab,
  searchableCategories,
  searchableTags,
}: SearchAndFilterFormProps) => {
  const { l10n } = useLocalization();
  const { formData, setFormData, onChange } = useFormData(searchFilters);

  const getAmountRangeError = (filters: PossibleFilters) => {
    const minAmount = parseAmount(filters?.minAmount);
    const maxAmount = parseAmount(filters?.maxAmount);

    if (minAmount === null || maxAmount === null || maxAmount >= minAmount)
      return "";
    return l10n.getString("error-max-less-than-min", null);
  };

  const amountRangeError = getAmountRangeError(formData);

  // organize categories & tags into the form that Dropdown expects its options to be in
  const categoryOptions = searchableCategories.map((category) => ({
    displayName: Filters.capitalizeFirstLetter(category),
    value: category,
  }));
  categoryOptions.unshift({
    displayName: l10n.getString(
      "filter-all-categories",
      null,
      "All categories"
    ),
    value: "all",
  });

  const tagOptions = searchableTags.map((tag) => ({
    displayName: tag,
    value: tag,
  }));
  tagOptions.unshift({
    displayName: l10n.getString("filter-all-tags", null, "All tags"),
    value: "all",
  });

  useEffect(() => {
    if (formData?.period !== "custom") {
      setFormData((prevState: PossibleFilters) => {
        const newState = { ...prevState };
        delete newState.min_date;
        delete newState.max_date;
        return newState;
      });
    }
  }, [formData?.period]);

  const clearForm = () => {
    // updates the search filters for the parent to also be `{}` if it wasn't empty before
    setFormData({});
    if (JSON.stringify(searchFilters) !== "{}") setSearchFilters({});
  };

  const submitSearchAndFilterForm = () => {
    if (amountRangeError) return;

    setSearchFilters(formData);
    /* After the user submits the search & filter form, the active tab will be switched to `All`.
       This is because `Recent` tab's transactions will not reflect the filtering.
     */
    switchToAllTransactionsTab();
    closeDialog();
  };

  return (
    <ContextForm data={formData} onChange={onChange}>
      <ContextForm.Field>
        <DropdownField
          options={presetPeriods()}
          field="period"
          label={l10n.getString("label-date-range", null, "Date range")}
        />
      </ContextForm.Field>
      <DateRangeFormField
        hidden={formData?.period !== "custom"}
        minDate={formData?.min_date}
        testIdPrefix="transaction-search-and-filter"
      />
      <div className="search-and-filter-border">
        <MinMaxAmountField errorText={amountRangeError} />
      </div>
      <div className="search-and-filter-border">
        <ContextForm.Field>
          <TextInput
            name="keyword"
            field="keyword"
            label={l10n.getString("label-keyword", null, "Keyword")}
            data-test-element="input-keyword"
          />
        </ContextForm.Field>
      </div>
      {categoryOptions.length > 1 && (
        <div className="search-and-filter-border">
          <ContextForm.Field>
            <DropdownField
              options={categoryOptions}
              field="category"
              label={l10n.getString("label-category", null, "Category")}
            />
          </ContextForm.Field>
        </div>
      )}
      <div className="search-and-filter-border">
        <ContextForm.Field>
          <DropdownField
            options={SearchTransactionTypeOptions}
            field="transaction_type"
            label={l10n.getString(
              "label-transaction-type",
              null,
              "Transaction type"
            )}
          />
        </ContextForm.Field>
      </div>
      {tagOptions.length > 1 && (
        <div className="search-and-filter-border">
          <ContextForm.Field>
            <DropdownField
              options={tagOptions}
              field="tag"
              label={l10n.getString("label-tag", null, "Tag")}
            />
          </ContextForm.Field>
        </div>
      )}
      <ContextForm.ActionBar>
        <ContextForm.Action onSubmit={clearForm} dangerouslyDisableShowLoading>
          <Button
            type="button"
            kind="plain"
            label={l10n.getString("button-clear-all", null, "Clear all")}
            testId="search-filter-form-clear-all-filters"
          />
        </ContextForm.Action>
        <ContextForm.Action
          onSubmit={submitSearchAndFilterForm}
          dangerouslyDisableShowLoading
        >
          <div style={{ marginLeft: "auto" }}>
            <Button
              kind="primary"
              label={l10n.getString(
                "button-show-results",
                null,
                "Show results"
              )}
              testId="show-results"
              data-test-element="button-show-results"
            />
          </div>
        </ContextForm.Action>
      </ContextForm.ActionBar>
    </ContextForm>
  );
};

const TransactionSearchAndFilter = ({
  searchFilters,
  setSearchFilters,
  switchToAllTransactionsTab,
  searchableCategories,
  searchableTags,
}: TransactionSearchAndFilterProps) => {
  const { l10n } = useLocalization();
  /* Necessary wrapper because if the search filter dialog state was kept inside of `TransactionCard`,
     the paginated transactions list would refresh every time the dialog state changed
     (i.e. when opening or closing) */
  const filterLength = convertFiltersToTags(searchFilters, formatNumber).length;
  const [isSearchFilterOpen, setIsSearchFilterOpen] = useState(false);
  const closeDialog = () => setIsSearchFilterOpen(false);
  const openDialog = () => setIsSearchFilterOpen(true);

  return (
    <>
      <Button
        kind="plain"
        label={
          filterLength > 0
            ? l10n.getString(
                "button-search-filter-with-count",
                { count: filterLength },
                "Search & filter ({ $count })"
              )
            : l10n.getString("button-search-filter", null, "Search & filter")
        }
        onClick={openDialog}
        testId="open-search-filter-dialog"
        data-test-element="button-search-filter"
      />
      <Dialog
        isOpen={isSearchFilterOpen}
        onUserDismiss={closeDialog}
        title={l10n.getString(
          "dialog-title-search-filter",
          null,
          "Search & filter"
        )}
      >
        <div className="margin--top--s">
          <SearchAndFilterForm
            closeDialog={closeDialog}
            searchFilters={searchFilters}
            setSearchFilters={setSearchFilters}
            switchToAllTransactionsTab={switchToAllTransactionsTab}
            searchableTags={searchableTags}
            searchableCategories={searchableCategories}
          />
        </div>
      </Dialog>
    </>
  );
};
export default TransactionSearchAndFilter;
